/**
 * Hosts {@link PayablesPanel} on the banking page.
 *
 * Connection-scoped, like the NSF queue, because that is the unit the
 * bank settles: one RSA code settles any number of items across every
 * account on the connection, so the day's batch is one screen, not one
 * per association.
 *
 * Owner/Admin only, matching `can_settle_payables` — and deliberately
 * not super admins. Nothing on this screen moves money until a person
 * types their one-time RSA code into the settle dialog.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Subheading } from './common/Catalyst/heading';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { ConnectionPicker } from './bank/linear/ConnectionPicker';
import { bankService } from './clients/ApiClients';
import { BankConnectionReturn } from './clients/rust-api-client';
import { getCompanyIdFromToken } from './utils/TokenUtils';
import { apiErrorMessage } from './utils/apiErrors';
import PayablesPanel from './PayablesPanel';

const PayablesSection: React.FC = () => {
  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const [connections, setConnections] = useState<BankConnectionReturn[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      const response = await bankService.listCompanyConnections({
        companyId,
      });
      const active = response.data.filter((c) => c.is_active);
      setConnections(active);
      // Open on the connection with the most waiting to settle.
      let first = active[0]?.id ?? null;
      if (active.length > 1) {
        const counts = await Promise.all(
          active.map((c) =>
            bankService
              .listConnectionPayables({ connectionId: c.id, status: 'pending' })
              .then((r) => r.data.pending)
              .catch(() => 0)
          )
        );
        const busiest = counts.indexOf(Math.max(...counts));
        if (counts[busiest] > 0) first = active[busiest].id;
      }
      setSelected((current) => current ?? first);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't load bank connections."));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="mt-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Subheading>ACH payments to settle</Subheading>
          <Text className="mt-1 text-sm">
            Vendor payments and homeowner collections the bank is holding.
            Nothing moves until you settle them with your RSA code — any number
            at once, usually the day’s batch.
          </Text>
        </div>
        {connections.length > 1 && (
          <div>
            <ConnectionPicker
              className="border border-line"
              connections={connections}
              value={selected}
              onChange={setSelected}
            />
          </div>
        )}
      </div>

      <ErrorAlert className="mt-4">{error}</ErrorAlert>
      {!loading && !error && connections.length === 0 && (
        <Text className="mt-4">
          No active bank connection. Add one above to see payables.
        </Text>
      )}
      {selected && <PayablesPanel connectionId={selected} />}
    </section>
  );
};

export default PayablesSection;
